/**
 * Opening an external activity for an asset (D78) — the host's half, shared by the player and the
 * studio.
 *
 * The activity — Focus-FreeTalkBot's speaking practice — is drawn **in the host's page, over the
 * asset's frame**, never inside it: the frame has no endpoint and no credential to reach it with
 * (invariant 1). Where it sits is one setting:
 *
 *   panel    over the whole activity area — the frame's box — with no bar: the bot's finish ends it
 *   inline   over the step's own placeholder, the box the asset reports inside its frame
 *
 * Both are the same element positioned from one rectangle, kept in place as the page scrolls or
 * resizes, so moving from one to the other is that one value and nothing else.
 *
 * **The bot** is reached through its own embed kit, as its reference host does
 * (Focus-FreeTalkBot, specs/025-focus-hendrx-embed): `<bot>/sdk/freetalk.js` is loaded once, then
 * `FreeTalk.createFreeTalk({ origin, assertion, host })` and `client.scenario(container, { scenarioId,
 * placement, completesComponent: false })`. The kit frames the bot and speaks its `ft/1` protocol;
 * this file maps its events onto the asset's statuses. `completesComponent` is false because the
 * bot is one step of a lesson: the lesson completes the platform's component, not the bot — so the
 * kit raises no completion record for the host to report.
 *
 * **The learner** reaches the bot only as a signed pass — the player's server signs it from Focus's
 * launch — which the host hands the kit, never the asset (invariant 3).
 *
 * With no bot address configured, a mock bot — spoken, sandboxed, built here — opens instead.
 */

import { isFreeTalkContent } from '@cet/asset-contract';
import type { ExternalBox, ExternalPayload, ExternalStatus, FreeTalkScenario } from '@cet/asset-contract';

export type ExternalPlacement = 'panel' | 'inline';

/** Where the host gets the learner's pass for the bot: a fresh one each time, or undefined. */
export interface FreeTalkPass {
  get(): Promise<string | undefined>;
}

export interface OpenExternalOptions {
  /** The asset's frame — what a panel covers, and what an inline box is measured from. */
  readonly frame: HTMLIFrameElement;
  /** The `open` the asset sent. */
  readonly payload: ExternalPayload;
  readonly placement: ExternalPlacement;
  /** The bot's origin (`https://freetalk.testing.cet.ac.il`). Absent or empty: the mock bot. */
  readonly url?: string;
  /** The learner's pass — required for the real bot, unused by the mock. */
  readonly pass?: FreeTalkPass;
  /** The platform page that frames this host, for the kit's diagnostics. Default: this origin. */
  readonly platformOrigin?: string;
  /**
   * Where the activity is: `open` once shown, then `finished` or `closed` — `unavailable` if it cannot.
   * With `finished`, what the bot handed over (D96).
   */
  readonly onStatus: (status: ExternalStatus, finish?: ExternalFinish) => void;
  /** A line for the host's trace (the kit's events, its errors). */
  readonly note?: (line: string) => void;
  /** The bar's Close, in the host's language. */
  readonly closeLabel?: string;
  /** The bar's title. */
  readonly title?: string;
}

/**
 * What came back with `finished` (D96): the bot's outcome body, as its kit's `finished` carries it
 * (Free-Talk 034 `conversationOutcome`; the body of its 033 `conversation_outcome`) — content
 * strings, enumerations, counts and ids, never the learner's words — or null when the bot had none
 * in time. The host reports it in the attempt's `completed`; it never reaches the asset (invariant 2).
 */
export interface ExternalFinish {
  readonly conversationOutcome: Record<string, unknown> | null;
}

export interface ExternalHandle {
  /** The step's placeholder moved (inline placement). */
  layout(box: ExternalBox): void;
  /** Take it down without a status — the asset closed it, or the frame is going. */
  close(): void;
}

/**
 * The pass the kit is given, fresh each time — the bot spends one per open. `sign`: the host's
 * signer for a platform launch (D78). `demo`, local work only: the bot's own demo signer.
 */
export function freeTalkPass(options: { sign?: () => Promise<string | undefined>; botOrigin: string; demo?: boolean; note?: (line: string) => void }): FreeTalkPass {
  const demo = async (): Promise<string | undefined> => {
    if (!options.demo || options.botOrigin === '') return undefined;
    try {
      const query = new URLSearchParams({ host: location.origin, useMode: 'independent', reg: 'cet-assets-dev' });
      const response = await fetch(`${options.botOrigin}/api/v1/launch/demo-assertion?${query}`, { credentials: 'omit' });
      if (!response.ok) { options.note?.(`free-talk demo pass: HTTP ${response.status}`); return undefined; }
      return ((await response.json()) as { assertion?: string }).assertion;
    } catch (error) {
      options.note?.(`free-talk demo pass failed · ${error instanceof Error ? error.message : String(error)}`);
      return undefined;
    }
  };
  return { get: async () => (await options.sign?.()) ?? demo() };
}

/**
 * The bot's origin, from the platform's own domain: a host on `*.testing.cet.ac.il` talks to the
 * testing bot, one on `*.cet.ac.il` to production's, and anything else — a laptop — to the testing
 * bot, never to a local one. `configured` (FREE_TALK_BOT_URL) overrides it; `mock` opens the mock bot.
 */
export function freeTalkOrigin(configured: string | undefined, hostname: string = location.hostname): string {
  const set = (configured ?? '').trim();
  if (set === 'mock') return '';
  if (set !== '') return set.replace(/\/$/, '');
  if (hostname.endsWith('.testing.cet.ac.il')) return 'https://freetalk.testing.cet.ac.il';
  if (hostname.endsWith('.cet.ac.il')) return 'https://freetalk.cet.ac.il';
  return 'https://freetalk.testing.cet.ac.il';
}

/** The kit's errors that end the talk before or while it runs: the asset is told `unavailable`. */
const STOPS = new Set([
  'launch_missing', 'launch_expired', 'launch_used', 'launch_invalid', 'not_recognised', 'not_permitted',
  'not_available', 'school_disabled', 'host_not_allowed', 'scenario_not_allowed', 'kit_outdated',
  'service_unavailable', 'load_failed', 'unsupported_browser', 'already_mounted',
]);

/** The screen the kit mounted — its TalkHandle. */
interface TalkHandle {
  end(reason?: string): void;
}

/** The parts of the bot's kit this host uses — FreeTalkClient in its embed-types.ts. */
interface FreeTalkClient {
  /** A catalog scenario by `scenarioId`, or the scenario itself as `scenario` — exactly one (its scenario/1). */
  scenario(container: HTMLElement, options: { scenarioId?: string; scenario?: FreeTalkScenario; placement?: 'component' | 'inline'; completesComponent?: boolean; frameTitle?: string }): TalkHandle;
  renewAssertion(jwt: string): void;
  on(event: string, handler: (payload: Record<string, unknown>) => void): () => void;
  unmount(): void;
}
type FreeTalkKit = { createFreeTalk(options: { origin: string; assertion?: string; host: { platformOrigin: string } }): Promise<FreeTalkClient> };

const kits = new Map<string, Promise<FreeTalkKit>>();
/** The bot's kit, loaded once per origin from the bot itself — the bot's current kit, no package. */
function loadKit(origin: string): Promise<FreeTalkKit> {
  const known = kits.get(origin);
  if (known) return known;
  const loading = new Promise<FreeTalkKit>((resolve, reject) => {
    const ready = (): FreeTalkKit | undefined => (window as unknown as { FreeTalk?: FreeTalkKit }).FreeTalk;
    if (ready()) { resolve(ready()!); return; }
    const script = document.createElement('script');
    script.src = `${origin}/sdk/freetalk.js`;
    // A plain script, as the bot's reference host loads it: the bot serves its kit without CORS headers,
    // so asking for it anonymously (crossorigin) would be refused.
    script.onload = () => (ready() ? resolve(ready()!) : reject(new Error(`${script.src} loaded but defined no FreeTalk`)));
    script.onerror = () => reject(new Error(`the kit did not load from ${script.src}`));
    document.head.append(script);
  });
  loading.catch(() => kits.delete(origin));
  kits.set(origin, loading);
  return loading;
}

/**
 * A panel is the bot's whole box, so the kit's screen fills it: no height floor of its own (bug
 * 540198). The kit holds a component's frame at 560px at least, which a host that grows the frame
 * to fit can use — but this layer is as tall as the activity area, so on a laptop the floor made
 * the layer scroll around a screen that scrolls itself. Taller content scrolls inside the screen.
 */
function fillPanel(stage: HTMLElement): void {
  const screen = stage.querySelector('iframe');
  if (screen !== null) Object.assign(screen.style, { flex: '1', minHeight: '0', alignSelf: 'stretch' } satisfies Partial<CSSStyleDeclaration>);
}

/** Where the activity is drawn, in the host page's viewport. */
function rectFor(frame: HTMLIFrameElement, placement: ExternalPlacement, box: ExternalBox | undefined): DOMRect {
  const outer = frame.getBoundingClientRect();
  if (placement === 'panel' || box === undefined || box.width === 0 || box.height === 0) return outer;
  return new DOMRect(outer.left + box.x, outer.top + box.y, Math.min(box.width, outer.width), box.height);
}

export function openExternal(options: OpenExternalOptions): ExternalHandle {
  const { frame, payload, placement } = options;
  const note = options.note ?? (() => {});
  const title = options.title ?? 'Speaking practice';
  let box = payload.box;
  let ended = false;
  let client: FreeTalkClient | null = null;
  let talk: TalkHandle | null = null;

  const layer = document.createElement('div');
  layer.className = 'cet-external';
  layer.dataset['placement'] = placement;
  layer.setAttribute('role', 'dialog');
  layer.setAttribute('aria-label', title);
  Object.assign(layer.style, {
    position: 'fixed', zIndex: '50', display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
    background: '#fff', border: '1px solid #e5e0d8', borderRadius: '16px', overflow: 'hidden',
    boxShadow: '0 18px 48px rgb(19 28 38 / 0.18)',
  } satisfies Partial<CSSStyleDeclaration>);

  // No bar of the host's own: the bot's screen is the whole layer, and its own finish is the way out.

  // Where the bot draws: the kit's frame, or the mock's.
  const stage = document.createElement('div');
  Object.assign(stage.style, { flex: '1', minHeight: '0', overflow: 'auto', display: 'flex' });
  layer.append(stage);

  const place = (): void => {
    const r = rectFor(frame, placement, box);
    Object.assign(layer.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });
  };
  const observer = new ResizeObserver(place);
  let onMock: ((event: MessageEvent) => void) | null = null;

  function end(status: ExternalStatus | null, finish?: ExternalFinish): void {
    if (ended) return;
    ended = true;
    // Leaving mid-call — Close, or the learner left the step — ends the talk first, as the kit asks
    // (`end('navigation')`); a finished talk has nothing left to end.
    if (status !== 'finished' && status !== 'unavailable') { try { talk?.end('navigation'); } catch { /* going anyway */ } }
    try { client?.unmount(); } catch { /* the kit is going anyway */ }
    if (onMock) window.removeEventListener('message', onMock);
    window.removeEventListener('resize', place);
    window.removeEventListener('scroll', place, true);
    observer.disconnect();
    layer.remove();
    if (status !== null) options.onStatus(status, finish);
  }

  window.addEventListener('resize', place);
  window.addEventListener('scroll', place, true);
  observer.observe(frame);
  document.body.append(layer);
  place();

  const activity = payload.activity;
  // The scenario itself when the step wrote one, else the bot's catalog scenario by key.
  const named = activity === undefined ? {} : isFreeTalkContent(activity) ? { scenario: activity } : { scenarioId: activity.scenarioId };
  const origin = (options.url ?? '').trim();
  if (origin === '') openMock(); else void openBot(origin.replace(/\/$/, ''));

  /** The real bot, through its kit. */
  async function openBot(botOrigin: string): Promise<void> {
    try {
      const assertion = await options.pass?.get();
      if (ended) return;
      if (!assertion) note('free-talk: no learner pass from the platform — the bot will ask for one');
      const kit = await loadKit(botOrigin);
      if (ended) return;
      const made = await kit.createFreeTalk({
        origin: botOrigin,
        ...(assertion ? { assertion } : {}),
        host: { platformOrigin: options.platformOrigin ?? location.origin },
      });
      if (ended) { made.unmount(); return; }
      client = made;
      made.on('ready', (e) => note(`free-talk ready · kit ${String(e['kitVersion'] ?? '?')} · ${String(e['contractVersion'] ?? '?')}`));
      made.on('agents', (e) => note(`free-talk agent · ${JSON.stringify(e['speaking'] ?? e)}`));
      // The talk is over: the step is done, as an answered question is (focus pages on from here).
      // D96: the bot's outcome body comes with it, for the attempt's `completed` — the asset never sees it.
      made.on('finished', (e) => {
        const body = e['conversationOutcome'];
        const conversationOutcome = typeof body === 'object' && body !== null && !Array.isArray(body) ? (body as Record<string, unknown>) : null;
        note(`free-talk finished · ${String(e['outcome'] ?? '')}${conversationOutcome === null ? ' · no outcome body' : ' · with its outcome'}`);
        end('finished', { conversationOutcome });
      });
      made.on('error', (e) => {
        const kind = String(e['kind'] ?? '');
        note(`free-talk error · ${kind} · ${String(e['message'] ?? '')}`);
        if (STOPS.has(kind)) end('unavailable');
      });
      made.on('fault', (e) => note(`free-talk fault · ${String(e['kind'] ?? '')}`));
      // The kit says this host's version is going away: a line for whoever upgrades it.
      made.on('deprecated', (e) => note(`free-talk kit deprecated${e['supportedUntil'] ? ` — supported until ${String(e['supportedUntil'])}` : ''}; upgrade the host`));
      made.on('needAssertion', () => {
        void (async () => {
          const fresh = await options.pass?.get();
          if (ended) return;
          if (fresh) made.renewAssertion(fresh);
          else { note('free-talk: the bot asked for a new learner pass, and this host has none'); end('unavailable'); }
        })();
      });
      // completesComponent false: no completion record — but should one come, it is the host's to report.
      made.on('learningEvent', (e) => note(`free-talk learning event ${String(e['statementId'] ?? '')} — not reported (completesComponent is false)`));
      talk = made.scenario(stage, { ...named, placement: placement === 'panel' ? 'component' : 'inline', completesComponent: false, frameTitle: title });
      if (placement === 'panel') fillPanel(stage);
      options.onStatus('open');
    } catch (error) {
      const kind = (error as { kind?: unknown } | null)?.kind;
      note(`free-talk could not open · ${typeof kind === 'string' ? `${kind} · ` : ''}${error instanceof Error ? error.message : String(error)}`);
      end('unavailable');
    }
  }

  /** The mock: opaque origin, scripts only — it can talk to us through postMessage and nothing else. */
  function openMock(): void {
    const bot = document.createElement('iframe');
    bot.title = title;
    bot.setAttribute('allow', 'microphone; autoplay');
    bot.setAttribute('sandbox', 'allow-scripts');
    Object.assign(bot.style, { flex: '1', width: '100%', border: '0' });
    bot.srcdoc = MOCK_BOT;
    const hand = (): void => bot.contentWindow?.postMessage({ type: 'cet:activity', activity: payload.activity }, '*');
    onMock = (event: MessageEvent): void => {
      if (event.source !== bot.contentWindow) return;
      const type = (event.data as { type?: unknown } | null)?.type;
      if (type === 'cet:ready') hand();
      if (type === 'cet:finished') end('finished', { conversationOutcome: null });
    };
    bot.addEventListener('load', hand);
    window.addEventListener('message', onMock);
    stage.append(bot);
    options.onStatus('open');
  }

  return {
    layout(next) {
      box = next;
      place();
    },
    close() {
      end(null);
    },
  };
}

/**
 * The mock free-talk bot — a **spoken** roleplay, as the real one is: the character's lines are read
 * aloud, the learner answers by voice (a big mic), and the talk shows as a transcript. It knows only
 * the scenario's key, so its lines are generic — the real bot plays the scenario itself. It says
 * `finished` when the learner taps Finish. Where the browser cannot listen inside the mock's sandbox,
 * the mic plays a pretend spoken turn, so the flow can be tried on any machine.
 */
const MOCK_BOT = `<!doctype html><html lang="en"><head><meta charset="utf-8"><style>
body{margin:0;font:15px/1.5 system-ui,sans-serif;color:#2b2f45;display:flex;flex-direction:column;height:100vh;background:#faf8f3}
header{padding:8px 14px;background:#fff8e7;font-size:13px;border-bottom:1px solid #e5e0d8} header b{display:block;font-size:13px}
#who{display:flex;flex-direction:column;align-items:center;gap:4px;padding:14px 14px 6px}
#face{width:72px;height:72px;border-radius:50%;background:#157A45;color:#fff;display:flex;align-items:center;justify-content:center;font:700 30px system-ui;transition:box-shadow .2s}
#face.talking{box-shadow:0 0 0 8px rgba(21,122,69,.18)}
#name{font-weight:700} #status{font-size:13px;color:#5a6668;min-height:20px}
#log{flex:1;overflow:auto;padding:8px 14px;display:flex;flex-direction:column;gap:8px}
.m{max-width:80%;padding:8px 12px;border-radius:14px;border:1px solid #e5e0d8}.bot{align-self:flex-start;background:#fff}.me{align-self:flex-end;background:#e8f3ec}
#bar{display:flex;align-items:center;justify-content:center;gap:16px;padding:12px 14px;border-top:1px solid #e5e0d8;background:#fff}
#mic{width:64px;height:64px;border-radius:50%;border:0;background:#157A45;color:#fff;font-size:26px;cursor:pointer}
#mic.on{background:#dc2626;box-shadow:0 0 0 8px rgba(220,38,38,.18)} #mic:disabled{opacity:.45}
#done{padding:8px 16px;border-radius:999px;border:2px solid #157A45;background:#fff;color:#157A45;font:600 14px system-ui;cursor:pointer}
</style></head><body>
<header id="brief">Mock bot — waiting for the activity…</header>
<div id="who"><div id="face">?</div><div id="name"></div><div id="status"></div></div>
<div id="log" aria-live="polite"></div>
<div id="bar"><button id="mic" aria-label="Talk" disabled>🎤</button><button id="done">Finish</button></div>
<script>
const el=(id)=>document.getElementById(id);const log=el('log');
const add=(t,c)=>{const d=document.createElement('div');d.className='m '+c;d.textContent=t;log.append(d);log.scrollTop=log.scrollHeight;};
const status=(t)=>{el('status').textContent=t;};
let a=null,turn=0,listening=false;
const Rec=self.SpeechRecognition||self.webkitSpeechRecognition;
function say(text){add(text,'bot');el('face').classList.add('talking');el('mic').disabled=true;status('Your partner is talking…');
  let over=false;const done=()=>{if(over)return;over=true;el('face').classList.remove('talking');el('mic').disabled=false;status('Your turn — tap the mic and speak.');};
  // However the voice behaves (none, or one that never says it ended), the turn passes after about
  // the time the line takes to say.
  setTimeout(done,Math.min(12000,1200+text.split(' ').length*380));
  try{const u=new SpeechSynthesisUtterance(text);u.lang='en-US';u.rate=.95;u.onend=done;u.onerror=done;speechSynthesis.cancel();speechSynthesis.speak(u);}catch(e){}}
const LINES=['Good idea! Tell me more.','Why do you think so?','What else can we do?','That sounds great. And then?'];
function reply(){const line=LINES[turn%LINES.length];turn++;
  setTimeout(()=>say(line),500);}
function heard(text){add(text,'me');reply();}
function listen(){if(listening)return;listening=true;el('mic').classList.add('on');status('Listening…');
  // A real phrase wins; an error, a silent end, or nothing within a few seconds plays a pretend
  // spoken turn — recognition inside this sandbox may not be allowed, and the flow must still run.
  let settled=false;
  const finish=(text)=>{if(settled)return;settled=true;clearTimeout(timer);listening=false;el('mic').classList.remove('on');heard(text);};
  const pretend=()=>finish('🎤 (mock) your spoken answer');
  const timer=setTimeout(pretend,4000);
  if(!Rec){setTimeout(pretend,1500);return;}
  try{const r=new Rec();r.lang='en-US';r.interimResults=false;r.onresult=(e)=>finish(e.results[0][0].transcript);r.onerror=pretend;r.onend=()=>setTimeout(pretend,300);r.start();}
  catch(e){setTimeout(pretend,1500);}}
addEventListener('message',(e)=>{if(e.source!==parent||!e.data||e.data.type!=='cet:activity'||a)return;a=e.data.activity||{};
  // The scenario itself when the step sent it; only its key otherwise (the real bot plays its own).
  const c=a.character||{};el('face').textContent=(c.name||'?').charAt(0).toUpperCase();el('name').textContent=(c.name||'Your partner')+(c.role?' · '+c.role:'');
  el('brief').innerHTML='';const b=document.createElement('b');b.textContent='Mock bot · '+(a.title||('scenario '+(a.scenarioId||'?')))+(a.level?' · '+a.level:'');
  const s=document.createElement('span');s.textContent=a.situation?(a.situation+(a.goals&&a.goals.length?' Goals: '+a.goals.join('; '):'')):'The real bot plays this catalog scenario with its own character, situation and goals.';el('brief').append(b,s);
  say(a.openingLine||"Hi! Let us talk. What do you want to plan?");});
el('mic').addEventListener('click',listen);
el('done').addEventListener('click',()=>{try{speechSynthesis.cancel();}catch(e){}parent.postMessage({type:'cet:finished'},'*');});
parent.postMessage({type:'cet:ready'},'*');
</script></body></html>`;
